import { Add, Check, DeleteOutlined, EditOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    IconButton,
    MenuItem,
    Paper,
    SvgIcon,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Zoom from '@mui/material/Zoom';
import { nanoid } from '@reduxjs/toolkit';
import cn from 'classnames';
import { useState, useEffect, useCallback, useRef, memo } from 'react';

import { ListTable, LTHeader, LTItem, LTRow, LTMobileItem } from 'components/common/ListTable';
import IconBtn from 'components/common/IconBtn';
import CustomizedTooltip from 'components/common/CustomizedTooltip/CustomizedTooltip';
import { ReactComponent as BasketIcon } from 'assets/icons/basket.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus-simple.svg';
import api, { MilestoneApp, MilestonePayload } from 'api';
import StyledTableInputWithError, { StyledTableInput } from './TableInput.styled';
import styles from './MilestonesAndFees.module.scss';
import DOMAIN from '../../../../../../../assets/domains.json';
import { DomainKey } from 'data/constants/domainApiKey';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { BiTrash } from 'react-icons/bi';
import { useWindowSize } from 'hooks/useWindowSize';
import { ReactComponent as DownloadIcon } from 'assets/icons/downloadB2b.svg';
import { ReactComponent as VisibilityOffIcon } from 'assets/icons/visibility_off.svg';
import { ReactComponent as SendArrowIcon } from 'assets/icons/sendArrow.svg';
import StatusDropdown from 'components/common/StatusDropdown/StatusDropdown';
import RetainerSection from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/MYCLIENTS/components/dashboard/salesOnboarding/components/RetainerSection/RetainerSection';
import GenericSearchableDropdown from 'components/common/Searchabledropdown/genericSearchableDropdown';
import { useParams } from 'react-router-dom';
import { currencyOptions } from 'utils/currencyOptions';
import { statusOptions } from 'data/constants/paymentStatuse';
import PDFViewer from 'components/common/PDFViewer/PDFViewer';

type StatusOption = {
    label: string;
    color: string;
    value?: string;
};

type MilestoneAndFee = Pick<
    MilestoneApp,
    'name' | 'description' | 'price' | 'currency' | 'application_id' | 'payment_status'
> & { id: string };

type Props = {
    milestones: MilestoneApp[];
    editMode: boolean;
    isCreating: boolean;
    onCurrentMilestonesList: (payload: MilestonePayload[]) => void;
    onHasErrors?: (value: boolean) => void;
    invoiceModal?: boolean;
    v2Style?: boolean;
};

const MAX_MILESTONES = 6;

const MilestonesAndFees = ({
    milestones,
    editMode,
    isCreating,
    onCurrentMilestonesList,
    onHasErrors,
    invoiceModal,
    v2Style = false,
}: Props) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [preparedMilestones, setPreparedMilestones] = useState<MilestoneAndFee[]>([]);
    const [showMaxMilestonesMessage, setShowMaxMilestonesMessage] = useState(false);
    const [name, setName] = useState<string>('');
    const [explanation, setExplanation] = useState<string>('');
    const [price, setPrice] = useState<string>('');
    const [currency, setCurrency] = useState<string>('USD');

    const [milestoneIdInEditMode, setMilestoneIdInEditMode] = useState<string>('');
    const [hasErrors, setErrors] = useState<boolean>(false);

    const [openMilNameTooltip, setOpenMilNameTooltip] = useState<string>('');
    const [openMilExplanationTooltip, setOpenMilExplanationTooltip] = useState<string>('');

    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);

    const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
    const params = useParams();
    const isRetainerNew = params['*']?.includes('/retainer');
    const isRetainerEditView = params['*']?.endsWith('/edit');
    const isPrevAgreementPage = params['*']?.includes('/agreement');

    const showInvoiceColumns = Boolean(invoiceModal);

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    const milestoneGridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: editMode
            ? showInvoiceColumns
                ? 'minmax(160px, 1fr) minmax(240px, 2fr) 120px 180px 180px 90px'
                : 'minmax(160px, 1fr) minmax(240px, 2fr) 120px 90px'
            : showInvoiceColumns
              ? 'minmax(160px, 1fr) minmax(240px, 2fr) 120px 180px 180px'
              : 'minmax(160px, 1fr) minmax(240px, 2fr) 120px',
        alignItems: 'center',
        width: '100%',
    };

    const milestoneCellLeftStyle: React.CSSProperties = {
        width: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textAlign: 'left',
    };

    const milestoneCellCenterStyle: React.CSSProperties = {
        width: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
    };

    // Set by the prop-sync effect below; the callback effect checks it to avoid
    // echoing prop changes back to the parent (which would cause a render loop).
    const isSyncingFromPropRef = useRef(false);

    useEffect(() => {
        if (isSyncingFromPropRef.current) {
            isSyncingFromPropRef.current = false;
            return;
        }
        onCurrentMilestonesList(preparedMilestones);
    }, [preparedMilestones, onCurrentMilestonesList]);

    useEffect(() => {
        isSyncingFromPropRef.current = true;
        convertData(milestones);
    }, [milestones, editMode]);

    const convertData = (arr: MilestoneApp[]) => {
        const converted = arr.map<MilestoneAndFee>((milestone) => ({
            id: milestone.id.toString(),
            name: milestone.name,
            description: milestone.description,
            price: milestone.price,
            currency: milestone.currency,
            application_id: milestone.application_id,
            payment_status: milestone.payment_status,
        }));

        setPreparedMilestones(converted);
    };

    const addMilestone = () => {
        if (preparedMilestones.length >= MAX_MILESTONES) {
            setShowMaxMilestonesMessage(true);
            return;
        }
        setShowMaxMilestonesMessage(false);
        const newMilestone: MilestoneAndFee = {
            id: nanoid(),
            name: name,
            description: explanation,
            price: price,
            application_id: 0,
            payment_status: '',
            currency: currency,
        };
        setPreparedMilestones((prevState) => [...prevState, newMilestone]);
        resetInputs();
    };

    const removeMilestone = (id: string) => {
        const filteredArr = preparedMilestones.filter((milestone) => milestone.id !== id);
        setPreparedMilestones(filteredArr);
        setShowMaxMilestonesMessage(false);
    };

    const handlePriceInput = (value: string) => {
        if (!isNaN(Number(value))) {
            setPrice(value);
        }
    };

    const resetInputs = () => {
        setName('');
        setExplanation('');
        setPrice('');
    };

    const getNamePlaceholder = (rowNumber: number) => {
        const commonString = t('milestoneName', { lng: language });

        switch (rowNumber) {
            case 0:
                return `${language === 'EN' ? 'First' : "D'abord"} ${commonString}`;
            case 1:
                return `${language === 'EN' ? 'Second' : 'Seconde'} ${commonString}`;
            case 2:
                return `${language === 'EN' ? 'Third' : 'Troisième'} ${commonString}`;
            case 3:
                return `${language === 'EN' ? 'Fourth' : 'Quatrième'} ${commonString}`;
            case 4:
                return `${language === 'EN' ? 'Fifth' : 'Cinquième'} ${commonString}`;
            case 5:
                return `${language === 'EN' ? 'Sixth' : 'Sixième'} ${commonString}`;
        }
    };

    const toggleRowInEditMode = (milId: string) => {
        if (milestoneIdInEditMode === milId) {
            setMilestoneIdInEditMode('');
            return;
        }
        setMilestoneIdInEditMode(milId);
    };

    const changeMilestone = (
        fieldName: 'name' | 'description' | 'price' | 'currency',
        value: string,
        milId: string
    ) => {
        if (fieldName === 'price' && isNaN(Number(value))) return;

        setPreparedMilestones((prevState) => {
            const targetMilestone = preparedMilestones.find((mil) => mil.id === milId);
            if (targetMilestone) targetMilestone[fieldName] = value;
            return [...prevState];
        });
    };

    const getTotalPrice = useCallback(() => {
        if (preparedMilestones) {
            const prices = preparedMilestones.map((mil) => Number(mil.price));
            const totalFee = prices.reduce<number>((acc, curr) => {
                const tmp = acc + curr;
                return tmp;
            }, 0);
            return totalFee;
        }
        return 0;
    }, [preparedMilestones]);

    const isAvailableFeeForFirstMilestone = useCallback(() => {
        if (DomainKey === DOMAIN.ALIRO) {
            const totalPrice = getTotalPrice();
            const allowedPriceForFirstFee = 20;
            const percentageOfTotalFirstMil =
                (Number(preparedMilestones[0]?.price) * 100) / Number(totalPrice);
            if (percentageOfTotalFirstMil < allowedPriceForFirstFee) {
                return false;
            }
            return true;
        } else {
            return true;
        }
    }, [getTotalPrice, preparedMilestones]);

    const getFeeStyles = (milIndex: number) => {
        if (milIndex === 0) {
            return cn({ [styles.danger]: !isAvailableFeeForFirstMilestone() });
        }
    };

    const validateMaxLength = (attribute: 'name' | 'explanation', value: string): string => {
        if (attribute === 'name' && value.length > 64) {
            return 'Max 64 characters allowed';
        }
        if (attribute === 'explanation' && value.length > 128) {
            return 'Max 128 characters allowed';
        }
        return '';
    };

    const validateInputLength = (attribute: 'name' | 'explanation', value: string): string => {
        if (value.length === 0) return 'Required field';

        return validateMaxLength(attribute, value);
    };

    const hasMilestonesErrors = useCallback((): boolean => {
        return preparedMilestones.some((mil) => {
            return (
                mil.name.length === 0 ||
                mil.description.length === 0 ||
                mil.price.length === 0 ||
                mil.currency.length === 0 ||
                mil.name.length > 64 ||
                mil.description.length > 128
            );
        });
    }, [preparedMilestones]);

    useEffect(() => {
        const hasArrayErrors: boolean = hasMilestonesErrors();
        if (onHasErrors) {
            onHasErrors(hasArrayErrors || !isAvailableFeeForFirstMilestone());
        }
        setErrors(hasArrayErrors);
    }, [preparedMilestones, hasMilestonesErrors, isAvailableFeeForFirstMilestone, onHasErrors]);

    // Tooltips
    const handleTooltipClose = (attribute: 'name' | 'explanation') => {
        setTimeout(() => {
            if (attribute === 'explanation') {
                setOpenMilExplanationTooltip('');
                return;
            }
            setOpenMilNameTooltip('');
        }, 270);
    };

    const handleTooltipOpen = (attribute: 'name' | 'explanation', milId: string) => {
        setTimeout(() => {
            if (attribute === 'explanation') {
                setOpenMilExplanationTooltip(milId);
                return;
            }
            setOpenMilNameTooltip(milId);
        }, 260);
    };

    const isCreateBtnEnable =
        name &&
        explanation &&
        price &&
        currency &&
        !validateMaxLength('name', name) &&
        !validateMaxLength('explanation', explanation);

    const sendInvoice = async (appId: string, milestoneId: string) => {
        try {
            await api.application.sendInvoiceEmail(appId, milestoneId);
        } catch (e) {
            console.error(e);
        }
    };

    const downloadInvoice = async (appId: string, milestoneId: string) => {
        try {
            const resp = await api.application.getMilestoneInvoice(appId, milestoneId);
            const blob = new Blob([resp.data], { type: 'application/pdf' });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `invoice-${milestoneId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.log(err);
        }
    };

    const viewInvoice = async (appId: string, milestoneId: string) => {
        try {
            const resp = await api.application.getMilestoneInvoice(appId, milestoneId);
            const blob = new Blob([resp.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            setInvoiceUrl(url);
        } catch (err) {
            console.error(err);
        }
    };

    const editMilestonePaymentStatus = async (
        appId: string,
        milestoneId: string,
        payload: string
    ) => {
        try {
            await api.application.editMilestonesPaymentStatus(appId, milestoneId, payload);
        } catch (err) {
            console.log(err);
        }
    };

    if (v2Style) {
        return (
            <Box>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                    <Table size="small" aria-label={t('createMilestones', { lng: language })}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, width: '28%' }}>
                                    {t('milestone', { lng: language })}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '36%' }}>
                                    {t('explanation', { lng: language })}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                    {t('fee', { lng: language })}
                                </TableCell>
                                {editMode ? (
                                    <TableCell sx={{ fontWeight: 700 }} align="center">
                                        {t('actions', { lng: language })}
                                    </TableCell>
                                ) : null}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {preparedMilestones.map((milestone, idx) => {
                                const isRowEditing = milestoneIdInEditMode === milestone.id;
                                const feeDanger = !isAvailableFeeForFirstMilestone() && idx === 0;
                                const nameError = validateInputLength('name', milestone.name);
                                const explanationError = validateInputLength(
                                    'explanation',
                                    milestone.description
                                );

                                return (
                                    <TableRow key={milestone.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {isRowEditing ? (
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    value={milestone.name}
                                                    placeholder={getNamePlaceholder(idx)}
                                                    error={Boolean(nameError)}
                                                    disabled={isCreating}
                                                    onChange={(event) =>
                                                        changeMilestone(
                                                            'name',
                                                            event.target.value,
                                                            milestone.id
                                                        )
                                                    }
                                                />
                                            ) : (
                                                milestone.name || '--'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {isRowEditing ? (
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    multiline
                                                    maxRows={3}
                                                    value={milestone.description}
                                                    placeholder={t('milestoneExplanation', {
                                                        lng: language,
                                                    })}
                                                    error={Boolean(explanationError)}
                                                    disabled={isCreating}
                                                    onChange={(event) =>
                                                        changeMilestone(
                                                            'description',
                                                            event.target.value,
                                                            milestone.id
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {milestone.description || '--'}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {isRowEditing ? (
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    value={milestone.price}
                                                    placeholder={t('fee', { lng: language })}
                                                    error={
                                                        milestone.price.length === 0 || feeDanger
                                                    }
                                                    disabled={isCreating}
                                                    sx={{ minWidth: 90 }}
                                                    onChange={(event) =>
                                                        changeMilestone(
                                                            'price',
                                                            event.target.value,
                                                            milestone.id
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color={feeDanger ? 'error' : 'inherit'}
                                                >
                                                    {milestone.currency} {milestone.price}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        {editMode ? (
                                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                                <IconButton
                                                    size="small"
                                                    disabled={hasErrors}
                                                    aria-label={t(isRowEditing ? 'save' : 'edit', {
                                                        lng: language,
                                                    })}
                                                    onClick={() =>
                                                        toggleRowInEditMode(milestone.id)
                                                    }
                                                >
                                                    {isRowEditing ? (
                                                        <Check fontSize="small" />
                                                    ) : (
                                                        <EditOutlined fontSize="small" />
                                                    )}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    aria-label={t('delete', { lng: language })}
                                                    onClick={() => removeMilestone(milestone.id)}
                                                >
                                                    <DeleteOutlined fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        ) : null}
                                    </TableRow>
                                );
                            })}
                            {editMode && preparedMilestones.length < MAX_MILESTONES ? (
                                <TableRow>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={name}
                                            placeholder={getNamePlaceholder(
                                                preparedMilestones.length
                                            )}
                                            error={Boolean(validateMaxLength('name', name))}
                                            disabled={isCreating}
                                            onChange={(event) => setName(event.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={explanation}
                                            placeholder={t('milestoneExplanation', {
                                                lng: language,
                                            })}
                                            error={Boolean(
                                                validateMaxLength('explanation', explanation)
                                            )}
                                            disabled={isCreating}
                                            onChange={(event) => setExplanation(event.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1, minWidth: 160 }}>
                                            <TextField
                                                select
                                                size="small"
                                                value={currency}
                                                disabled={isCreating}
                                                onChange={(event) =>
                                                    setCurrency(event.target.value)
                                                }
                                                sx={{ width: 90 }}
                                            >
                                                {currencyOptions.map((option) => (
                                                    <MenuItem
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <TextField
                                                size="small"
                                                value={price}
                                                placeholder={t('fee', { lng: language })}
                                                disabled={isCreating}
                                                sx={{ width: 80 }}
                                                onChange={(event) =>
                                                    handlePriceInput(event.target.value)
                                                }
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            disabled={!isCreateBtnEnable || isCreating}
                                            aria-label={t('addMilestone', { lng: language })}
                                            onClick={() => addMilestone()}
                                        >
                                            {isCreating ? (
                                                <CircularProgress size={16} />
                                            ) : (
                                                <Add fontSize="small" />
                                            )}
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableContainer>
                {editMode && preparedMilestones.length >= MAX_MILESTONES ? (
                    <Box sx={{ mt: 1.5 }}>
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<Add />}
                            onClick={() => setShowMaxMilestonesMessage(true)}
                            sx={{ textTransform: 'none' }}
                        >
                            {t('addMilestone', { lng: language })}
                        </Button>
                        {showMaxMilestonesMessage ? (
                            <Alert severity="info" sx={{ mt: 1 }}>
                                {t('youCanAddMilestones', { lng: language })}
                            </Alert>
                        ) : null}
                    </Box>
                ) : null}
                {!isAvailableFeeForFirstMilestone() ? (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {t('theFirstMilestoneMust', { lng: language })}
                    </Typography>
                ) : null}
            </Box>
        );
    }

    return (
        <div className={styles['table-wrapper']}>
            <ListTable style={{ position: 'relative' }}>
                {isRetainerNew || isRetainerEditView || isPrevAgreementPage ? (
                    <RetainerSection
                        title={t('createMilestones', { lng: language })}
                    ></RetainerSection>
                ) : (
                    <LTHeader innerStyle={mobile ? undefined : milestoneGridStyle}>
                        <LTItem style={milestoneCellLeftStyle}>
                            {t('milestone', { lng: language })}
                        </LTItem>
                        <LTItem style={milestoneCellLeftStyle}>
                            {t('explanation', { lng: language })}
                        </LTItem>
                        <LTItem style={milestoneCellCenterStyle}>
                            {t('fee', { lng: language })}
                        </LTItem>
                        {showInvoiceColumns && (
                            <>
                                <LTItem style={milestoneCellCenterStyle}>
                                    {t('status', { lng: language })}
                                </LTItem>
                                <LTItem style={milestoneCellCenterStyle}>
                                    {t('actions', { lng: language })}
                                </LTItem>
                            </>
                        )}
                        {editMode && (
                            <LTItem style={milestoneCellCenterStyle}>
                                {t('actions', { lng: language })}
                            </LTItem>
                        )}
                    </LTHeader>
                )}
                {preparedMilestones.map((milestone, idx, arr) => (
                    <LTRow
                        key={milestone.id}
                        style={{ position: 'relative' }}
                        innerStyle={mobile ? undefined : milestoneGridStyle}
                    >
                        {/*FULL SCREEN VERSION*/}
                        <LTItem
                            style={{
                                ...milestoneCellLeftStyle,
                                paddingRight: '8px',
                                overflow: 'visible',
                            }}
                        >
                            {milestoneIdInEditMode === milestone.id ? (
                                <StyledTableInputWithError
                                    value={milestone.name}
                                    onChange={(event) =>
                                        changeMilestone('name', event.target.value, milestone.id)
                                    }
                                    placeholder={getNamePlaceholder(idx)}
                                    errorMsg={validateInputLength('name', milestone.name)}
                                />
                            ) : (
                                <CustomizedTooltip
                                    sx={{
                                        '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip':
                                            { marginTop: '8px' },
                                    }}
                                    disableTouchListener
                                    onClose={() => handleTooltipClose('name')}
                                    open={openMilNameTooltip === milestone.id}
                                    title={milestone.name}
                                    TransitionComponent={Zoom}
                                    TransitionProps={{ timeout: 240 }}
                                    placement="bottom-start"
                                >
                                    <span
                                        className={cn(
                                            styles['table-row'],
                                            styles['table-row__name']
                                        )}
                                        onClick={() => handleTooltipOpen('name', milestone.id)}
                                        onMouseEnter={() => handleTooltipOpen('name', milestone.id)}
                                        onMouseLeave={() => handleTooltipClose('name')}
                                    >
                                        {milestone.name}
                                    </span>
                                </CustomizedTooltip>
                            )}
                        </LTItem>
                        <LTItem
                            style={{
                                ...milestoneCellLeftStyle,
                                paddingRight: '8px',
                                overflow: 'visible',
                            }}
                        >
                            {milestoneIdInEditMode === milestone.id ? (
                                <StyledTableInputWithError
                                    value={milestone.description}
                                    placeholder={t('milestoneExplanation', { lng: language })}
                                    onChange={(event) =>
                                        changeMilestone(
                                            'description',
                                            event.target.value,
                                            milestone.id
                                        )
                                    }
                                    errorMsg={validateInputLength(
                                        'explanation',
                                        milestone.description
                                    )}
                                />
                            ) : (
                                <CustomizedTooltip
                                    sx={{
                                        '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip':
                                            { marginTop: '8px' },
                                    }}
                                    disableTouchListener
                                    onClose={() => handleTooltipClose('explanation')}
                                    open={openMilExplanationTooltip === milestone.id}
                                    title={milestone.description}
                                    TransitionComponent={Zoom}
                                    TransitionProps={{ timeout: 240 }}
                                    placement="bottom-start"
                                >
                                    <span
                                        className={cn(
                                            styles['table-row'],
                                            styles['table-row__desc']
                                        )}
                                        onClick={() =>
                                            handleTooltipOpen('explanation', milestone.id)
                                        }
                                        onMouseEnter={() =>
                                            handleTooltipOpen('explanation', milestone.id)
                                        }
                                        onMouseLeave={() => handleTooltipClose('explanation')}
                                    >
                                        {milestone.description}
                                    </span>
                                </CustomizedTooltip>
                            )}
                        </LTItem>

                        {/* <LTItem
                            maxWidth="70px"
                            minWidth="none"
                            style={{ paddingRight: '8px', overflow: 'visible' }}>
                            {milestoneIdInEditMode === milestone.id ? (
                                <GenericSearchableDropdown
                                    selectedType={milestone.currency}
                                    handleSelectedType={(value) =>
                                        changeMilestone('currency', value, milestone.id)
                                    }
                                    dropdownMenuPrograms={currencyOptions}
                                    purple={true}
                                    width="96px"
                                />
                            ) : (
                                <span className={getFeeStyles(idx)}>{milestone.currency}</span>
                            )}
                        </LTItem> */}
                        <LTItem
                            style={{
                                ...milestoneCellCenterStyle,
                                paddingRight: '8px',
                                overflow: 'visible',
                            }}
                        >
                            {milestoneIdInEditMode === milestone.id ? (
                                <StyledTableInputWithError
                                    value={milestone.price}
                                    placeholder={t('fee', { lng: language })}
                                    padding="7px 10px"
                                    dangerColor={!isAvailableFeeForFirstMilestone() && idx === 0}
                                    onChange={(event) =>
                                        changeMilestone('price', event.target.value, milestone.id)
                                    }
                                    errorMsg={milestone.price.length === 0 ? 'Required' : undefined}
                                />
                            ) : (
                                <span className={getFeeStyles(idx)}>
                                    {milestone.currency}{' '}
                                    {milestone.price.toString().includes('.')
                                        ? `${milestone.price}0`
                                        : `${milestone.price}`}
                                </span>
                            )}
                        </LTItem>
                        {showInvoiceColumns && (
                            <>
                                <LTItem style={milestoneCellCenterStyle}>
                                    <StatusDropdown
                                        options={statusOptions}
                                        onChange={(selected: StatusOption) => {
                                            const nextValue = selected.value ?? '';
                                            if (!nextValue) return;
                                            editMilestonePaymentStatus(
                                                milestone.application_id.toString(),
                                                milestone?.id,
                                                nextValue
                                            );
                                        }}
                                        initialValue={
                                            milestone?.payment_status
                                                ? milestone?.payment_status
                                                : 'PENDING'
                                        }
                                    />
                                </LTItem>
                                <LTItem style={milestoneCellCenterStyle}>
                                    <IconBtn
                                        variant="primary"
                                        onClick={() => {
                                            viewInvoice(
                                                milestone.application_id.toString(),
                                                milestone?.id
                                            );
                                        }}
                                    >
                                        <VisibilityOffIcon fill={'#282828'} />
                                    </IconBtn>
                                    <IconBtn
                                        variant="primary"
                                        onClick={() => {
                                            downloadInvoice(
                                                milestone.application_id.toString(),
                                                milestone?.id
                                            );
                                        }}
                                    >
                                        <DownloadIcon fill={'#282828'} />
                                    </IconBtn>
                                    <IconBtn
                                        variant="primary"
                                        onClick={() => {
                                            if (
                                                milestone?.application_id !== undefined &&
                                                milestone?.id !== undefined
                                            ) {
                                                sendInvoice(
                                                    milestone.application_id.toString(),
                                                    milestone.id
                                                );
                                            }
                                        }}
                                    >
                                        <SendArrowIcon fill={'#282828'} />
                                    </IconBtn>
                                </LTItem>
                            </>
                        )}
                        {editMode && (
                            <LTItem style={milestoneCellCenterStyle}>
                                <IconBtn
                                    variant="outlined"
                                    disabled={hasErrors}
                                    onClick={() => toggleRowInEditMode(milestone.id)}
                                >
                                    {milestoneIdInEditMode === milestone.id ? (
                                        <CheckIcon
                                            style={{
                                                transform: 'scale(1.3)',
                                                cursor: 'pointer',
                                                fontSize: 'unset',
                                            }}
                                            fill={hasErrors ? '#C0C0C0' : '#282828'}
                                        />
                                    ) : (
                                        <EditIcon
                                            style={{
                                                transform: 'scale(1.3)',
                                                width: '19',
                                                height: '18px',
                                                cursor: 'pointer',
                                                fontSize: 'unset',
                                            }}
                                            fill={hasErrors ? '#C0C0C0' : '#282828'}
                                        />
                                    )}
                                </IconBtn>
                                <IconBtn
                                    ml="6px"
                                    variant="outlined"
                                    onClick={() => removeMilestone(milestone.id)}
                                >
                                    <BasketIcon fill="#282828" />
                                </IconBtn>
                            </LTItem>
                        )}

                        {/*SMALL SCREEN VERSION*/}
                        <div style={mobile ? { height: '35px' } : { display: 'none' }}>
                            <LTMobileItem name={t('name', { lng: language })}>
                                {milestoneIdInEditMode === milestone.id ? (
                                    <StyledTableInputWithError
                                        value={milestone.name}
                                        padding="4px 12px"
                                        onChange={(event) =>
                                            changeMilestone(
                                                'name',
                                                event.target.value,
                                                milestone.id
                                            )
                                        }
                                        placeholder={getNamePlaceholder(idx)}
                                        errorMsg={validateInputLength('name', milestone.name)}
                                    />
                                ) : (
                                    milestone.name
                                )}
                            </LTMobileItem>
                        </div>

                        <div style={mobile ? { height: '35px' } : { display: 'none' }}>
                            <LTMobileItem name={t('explanation', { lng: language })}>
                                {milestoneIdInEditMode === milestone.id ? (
                                    <StyledTableInputWithError
                                        value={milestone.description}
                                        placeholder={t('milestoneExplanation', { lng: language })}
                                        padding="4px 12px"
                                        onChange={(event) =>
                                            changeMilestone(
                                                'description',
                                                event.target.value,
                                                milestone.id
                                            )
                                        }
                                        errorMsg={validateInputLength(
                                            'explanation',
                                            milestone.description
                                        )}
                                    />
                                ) : (
                                    milestone.description
                                )}
                            </LTMobileItem>
                        </div>

                        <div style={mobile ? { height: '35px' } : { display: 'none' }}>
                            <LTMobileItem name={t('fee', { lng: language })}>
                                {milestoneIdInEditMode === milestone.id ? (
                                    <StyledTableInputWithError
                                        value={milestone.price}
                                        placeholder={t('fee', { lng: language })}
                                        padding="4px 12px"
                                        dangerColor={
                                            !isAvailableFeeForFirstMilestone() && idx === 0
                                        }
                                        onChange={(event) =>
                                            changeMilestone(
                                                'price',
                                                event.target.value,
                                                milestone.id
                                            )
                                        }
                                        errorMsg={
                                            milestone.price.length === 0 ? 'Required' : undefined
                                        }
                                    />
                                ) : (
                                    <span className={getFeeStyles(idx)}>
                                        {milestone.price.toString().includes('.')
                                            ? `${milestone.price}0`
                                            : `${milestone.price}`}
                                    </span>
                                )}
                            </LTMobileItem>
                        </div>

                        {editMode && (
                            <div style={mobile ? { height: '35px' } : { display: 'none' }}>
                                <LTMobileItem
                                    style={
                                        milestoneIdInEditMode === milestone.id
                                            ? { marginRight: '5px' }
                                            : {}
                                    }
                                    name={t('actions', { lng: language })}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <IconBtn
                                            style={{ padding: '0px', marginRight: '6px' }}
                                            disabled={hasErrors}
                                            onClick={() => toggleRowInEditMode(milestone.id)}
                                        >
                                            {milestoneIdInEditMode === milestone.id ? (
                                                <CheckIcon
                                                    style={{
                                                        transform: 'scale(1.3)',
                                                        cursor: 'pointer',
                                                        fontSize: 'unset',
                                                    }}
                                                    fill={hasErrors ? '#C0C0C0' : '#282828'}
                                                />
                                            ) : (
                                                <EditIcon
                                                    style={{
                                                        transform: 'scale(1.3)',
                                                        width: '19',
                                                        height: '18px',
                                                        cursor: 'pointer',
                                                        fontSize: 'unset',
                                                    }}
                                                    fill={hasErrors ? '#C0C0C0' : '#282828'}
                                                />
                                            )}
                                        </IconBtn>
                                        <IconBtn
                                            style={{ padding: '0px' }}
                                            onClick={() => removeMilestone(milestone.id)}
                                        >
                                            {/* <BasketIcon fill="#282828" /> */}
                                            <SvgIcon
                                                style={{
                                                    width: '15',
                                                    height: '16.7px',
                                                    cursor: 'pointer',
                                                    fontSize: 'unset',
                                                }}
                                                viewBox="3 5 18 16.7"
                                                component={BiTrash}
                                            />
                                        </IconBtn>
                                    </div>
                                </LTMobileItem>
                            </div>
                        )}
                    </LTRow>
                ))}

                {/* CREATE NEW MILESTONE */}
                {preparedMilestones.length < MAX_MILESTONES && editMode && (
                    <>
                        {/*FULL SCREEN VERSION*/}
                        <LTRow>
                            <LTItem
                                maxWidth="30%"
                                minWidth="none"
                                style={{ paddingRight: '8px', overflow: 'visible' }}
                            >
                                <StyledTableInputWithError
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    placeholder={getNamePlaceholder(preparedMilestones.length)}
                                    errorMsg={validateMaxLength('name', name)}
                                />
                            </LTItem>
                            <LTItem
                                maxWidth="36%"
                                minWidth="none"
                                style={{ paddingRight: '8px', overflow: 'visible' }}
                            >
                                <StyledTableInputWithError
                                    value={explanation}
                                    onChange={(event) => setExplanation(event.target.value)}
                                    placeholder={t('milestoneExplanation', { lng: language })}
                                    errorMsg={validateMaxLength('explanation', explanation)}
                                />
                            </LTItem>

                            <LTItem maxWidth="20%" minWidth="120px" style={{ paddingRight: '8px' }}>
                                <GenericSearchableDropdown
                                    selectedType={currency}
                                    handleSelectedType={(value) => {
                                        setCurrency(value);
                                    }}
                                    dropdownMenuPrograms={currencyOptions}
                                    purple={true}
                                    width="100%"
                                />
                            </LTItem>
                            <LTItem maxWidth="70px" minWidth="none" style={{ paddingRight: '8px' }}>
                                <StyledTableInput
                                    value={price}
                                    placeholder={t('fee', { lng: language })}
                                    padding="7px 10px"
                                    onChange={(event) => handlePriceInput(event.target.value)}
                                />
                            </LTItem>
                            <LTItem maxWidth="80px">
                                <IconBtn
                                    variant="outlined"
                                    onClick={() => addMilestone()}
                                    disabled={!isCreateBtnEnable}
                                >
                                    {!isCreating && (
                                        <PlusIcon
                                            fill={isCreateBtnEnable ? '#282828' : '#C0C0C0'}
                                        />
                                    )}
                                    {isCreating && (
                                        <div className={styles.loader}>
                                            <CircularProgress size={14} color="primary" />
                                        </div>
                                    )}
                                </IconBtn>
                            </LTItem>

                            {/*SMALL SCREEN VERSION*/}
                            <div className={styles['input-wrapper']}>
                                <div className={styles['input-wrapper_mobile']}>
                                    <StyledTableInputWithError
                                        value={name}
                                        onChange={(event) => setName(event.target.value)}
                                        placeholder={getNamePlaceholder(preparedMilestones.length)}
                                        errorMsg={validateMaxLength('name', name)}
                                    />
                                </div>

                                <div className={styles['input-wrapper_mobile']}>
                                    <StyledTableInputWithError
                                        value={explanation}
                                        onChange={(event) => setExplanation(event.target.value)}
                                        placeholder={t('milestoneExplanation', { lng: language })}
                                        errorMsg={validateMaxLength('explanation', explanation)}
                                    />
                                </div>
                                <div className={styles['input-wrapper_mobile']}>
                                    <GenericSearchableDropdown
                                        selectedType={currency}
                                        handleSelectedType={(value) => {
                                            setCurrency(value);
                                        }}
                                        dropdownMenuPrograms={currencyOptions}
                                        purple={true}
                                        width="100%"
                                    />
                                </div>

                                <div className={styles['input-wrapper__action']}>
                                    <StyledTableInput
                                        value={price}
                                        placeholder={t('fee', { lng: language })}
                                        padding="7px 14px"
                                        width="80px"
                                        onChange={(event) => handlePriceInput(event.target.value)}
                                    />
                                    <IconBtn
                                        variant="outlined"
                                        onClick={() => addMilestone()}
                                        disabled={!isCreateBtnEnable}
                                    >
                                        {!isCreating && (
                                            <PlusIcon
                                                fill={isCreateBtnEnable ? '#282828' : '#C0C0C0'}
                                            />
                                        )}
                                        {isCreating && (
                                            <div className={styles.loader}>
                                                <CircularProgress size={14} color="primary" />
                                            </div>
                                        )}
                                    </IconBtn>
                                </div>
                            </div>
                        </LTRow>
                    </>
                )}

                {invoiceUrl && (
                    <div
                        style={{
                            marginTop: '1rem',
                            border: '1px solid #ccc',
                            borderRadius: '8px',
                            overflowY: 'auto',
                            width: '100%',
                            height: '600px',
                        }}
                    >
                        <PDFViewer file={invoiceUrl} />
                    </div>
                )}
            </ListTable>
            {!isAvailableFeeForFirstMilestone() && (
                <span className={styles.msg}>{t('theFirstMilestoneMust', { lng: language })}</span>
            )}
        </div>
    );
};

export default memo(MilestonesAndFees);
